import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ASSIGNABLE_ROLES, useAssignableRoles } from '@/lib/can-manage-role';
import { grantErrorMessage, useCustomRoles } from '@/lib/custom-roles';
import { ROLE_LABELS } from '@/lib/role-display';
import { useInviteMember } from '@/lib/users';

// Aligné sur inviteSchema (tenants-service, routes/users/invite.controller.ts)
const schema = z.object({
  email: z.string().trim().toLowerCase().email('Email invalide'),
  role: z.enum(ASSIGNABLE_ROLES, { errorMap: () => ({ message: 'Rôle requis' }) }),
  customRoleId: z.string().optional(),
});

// Radix Select n'accepte pas de valeur vide
const NO_CUSTOM_ROLE = 'none';

type FormData = z.infer<typeof schema>;

interface Props { open: boolean; onOpenChange: (open: boolean) => void }

export function InviteMemberDialog({ open, onOpenChange }: Props) {
  const roles = useAssignableRoles();
  const invite = useInviteMember();
  const { data: customRoles } = useCustomRoles(open);
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { email: '', role: 'VIEWER' } });

  const resetMutation = invite.reset;
  useEffect(() => {
    if (open) { reset({ email: '', role: 'VIEWER', customRoleId: undefined }); resetMutation(); }
  }, [open, reset, resetMutation]);

  const customRoleId = watch('customRoleId');
  const submit = (d: FormData) =>
    invite.mutate({ email: d.email, role: d.role, ...(d.customRoleId ? { customRoleId: d.customRoleId } : {}) }, {
      onSuccess: () => {
        toast.success(`Invitation envoyée à ${d.email}`);
        onOpenChange(false);
      },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Inviter un membre</DialogTitle>
          <DialogDescription>Un email avec un lien d&apos;activation valable 7 jours lui sera envoyé.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div>
            <Label htmlFor="invite-email">Email *</Label>
            <Input id="invite-email" type="email" autoComplete="off" {...register('email')} placeholder="prenom.nom@entreprise.gn" />
            {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <Label htmlFor="invite-role">Rôle *</Label>
            <Select value={watch('role')} onValueChange={(v) => setValue('role', v as FormData['role'], { shouldValidate: true })}>
              <SelectTrigger id="invite-role" aria-label="Rôle"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-xs text-danger mt-1">{errors.role.message}</p>}
            {customRoleId && (
              <p className="text-xs text-muted-foreground mt-1">
                Rôle de repli en cas de suppression du rôle personnalisé avant l&apos;acceptation.
              </p>
            )}
          </div>
          {!!customRoles?.data.length && (
            <div>
              <Label htmlFor="invite-custom-role">Rôle personnalisé</Label>
              <Select
                value={customRoleId ?? NO_CUSTOM_ROLE}
                onValueChange={(v) => setValue('customRoleId', v === NO_CUSTOM_ROLE ? undefined : v)}
              >
                <SelectTrigger id="invite-custom-role" aria-label="Rôle personnalisé"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CUSTOM_ROLE}>Aucun (rôle standard)</SelectItem>
                  {customRoles.data.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {invite.isError && (
            <p role="alert" className="text-sm text-danger">{grantErrorMessage(invite.error, 'Erreur lors de l’invitation')}</p>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={invite.isPending}>{invite.isPending ? 'Envoi…' : 'Envoyer l’invitation'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
